import Vue from "vue";
import Vuex from "vuex";
import { ColorUpdater } from "bulma-css-vars";
import { bulmaCssVariablesDefs } from "@/bulma-generated/bulma-colors";
import { db } from "@/firebase";
import { auth } from "@/firebase";
import cloneDeep from "clone-deep";
import mediasoupConfigProd from "../mediasoup-client/endpoint_cfgs/production.json";
import mediasoupConfigDev from "../mediasoup-client/endpoint_cfgs/development.json";
import Logger from "../mediasoup-client/Logger";

const logger = new Logger(`/store/index.js`);

const mediasoupConfig = process.env.NODE_ENV === "development" ? mediasoupConfigDev : mediasoupConfigProd;
// const mediasoupConfig = process.env.NODE_ENV === "development" ? mediasoupConfigDev : mediasoupConfigDev;
logger.info(`APP ENVIRONMENT: ${process.env.NODE_ENV}`);
logger.info(`MEDIASOUP EXPRESS API URL: `, mediasoupConfig.MediasoupExpressAppUrl);

const updater = new ColorUpdater(bulmaCssVariablesDefs);

Vue.use(Vuex);

const firebaseAuthPlugin = (store) => {
  auth.onAuthStateChanged(async (user) => {
    store.commit("SET_USER", user);
    if (user) {
      if ((await db.collection("platform_admins").doc(user.uid).get()).exists) {
        store.commit("SET_PERMISSION", "platform");
      }
    }
  });
};

export default new Vuex.Store({
  state: {
    organizationId: undefined,
    organizationName: "",
    organizationData: {},
    user: auth.currentUser,
    permissions: [],
    branding: {
      primaryColor: "#121C2E",
      secondaryColor: "#121C2E",
      tertiaryColor: "#121C2E",
      fourthColor: "#121C2E",
      brandLogo: "/assets/qondalogo.svg",
    },
    draft: {
      description: "",
      eventTimes: [],
      isVideoEvent: true,
      langComb: [],
      maxParticipants: 2,
      name: "",
      organizer: "",
      billNote: "",
      paymentMethod: "",
      livestreamType: "player",
      hybridType: "livestream",
      interpretingType: "device",
      interpretingParticipants: 2,
      reqToSpeakMode: false,
      branding: {
        primaryColor: "#121C2E",
        secondaryColor: "#121C2E",
        tertiaryColor: "#121C2E",
        fourthColor: "#121C2E",
        brandLogo: "/assets/qondalogo.svg",
      },
      isSubscriptionEvent: false,
      subscriptionDetails: undefined,
      subscriptionTrial: undefined,
    },
    moderationPassword: "",
    survey: {
      questions: [],
      options: {
        timed: false,
        duration: 60,
        showResults: true,
        personalization: false,
      },
      persons: [],
      title: "",
      draft: true,
    },
    transcribeWebsocket: null,
    transcribeWebsocketReady: false,
    audioContextDest: null,
    routeTosubsPage: false,
  },
  mutations: {
    // Receives an object containing subscription details
    set_subscriptionDetails(state, payload) {
      state.subscriptionDetails = payload;
    },
    set_subscriptionTrial(state, payload) {
      state.subscriptionTrial = payload;
    },
    set_routeTosubsPage(state, payload) {
      state.routeTosubsPage = payload;
    },
    set_audioContextDest(state, payload) {
      state.audioContextDest = payload;
    },
    set_transcribeWebsocket(state, payload) {
      state.transcribeWebsocket = payload;
    },
    set_transcribeWebsocketReady(state, payload) {
      state.transcribeWebsocketReady = payload;
    },
    CHANGE_COLOR_SCHEME(state, { primaryColor, secondaryColor, tertiaryColor, fourthColor }) {
      if (primaryColor) {
        updater.updateVarsInDocument("primary", primaryColor);
        state.branding.primaryColor = primaryColor;
      }
      if (secondaryColor) {
        updater.updateVarsInDocument("link", secondaryColor);
        state.branding.secondaryColor = secondaryColor;
      }
      if (tertiaryColor) {
        //updater.updateVarsInDocument('', tertiaryColor)
        state.branding.tertiaryColor = tertiaryColor;
      }
      if (fourthColor) {
        //updater.updateVarsInDocument('', fourthColor)
        state.branding.fourthColor = fourthColor;
      }
    },
    CHANGE_LOGO(state, logoUrl) {
      if (logoUrl != "") {
        // if set
        state.branding.brandLogo = logoUrl;
      }
    },
    SET_ORGANIZATION_ID(state, organizationId) {
      state.organizationId = organizationId;
    },
    SET_ORGANIZATION_NAME(state, organizationName) {
      state.organizationName = organizationName;
    },
    SET_ORGANIZATION_DATA(state, organizationData) {
      state.organizationData = organizationData;
    },
    SET_USER(state, user) {
      state.user = user;
    },
    SET_PERMISSION(state, organizationId) {
      state.permissions.push(organizationId);
    },
    FLUSH_PERMISSIONS(state) {
      state.permissions = [];
    },
    LOAD_SURVEY_DATA(state, survey) {
      state.survey = cloneDeep(survey);
    },
    LOAD_DRAFT_DATA(state, draft) {
      state.draft = draft;
      // filter out dates that are before the current date
      // these saves are now only local and only get saved on changes
      /* removed temporarily because with this there are not "today events" possible
			state.draft.eventTimes = draft.eventTimes.filter(y => {
				if (new Date(y.date.toDate()) > new Date()) {
					return true
				}
				return false
			})
			*/
      state.draft.eventTimes = state.draft.eventTimes.map((y) => {
        return {
          date: y.date.toDate(),
          startTime: y.startTime.toDate(),
          endTime: y.endTime.toDate(),
        };
      });

      state.draft.langComb = draft.langComb.map((y) => {
        return {
          lang1: y.lang1,
          lang2: y.lang2,
          mono: y.mono,
          timed: y.timed,
          da24: y.da24,
          timeslots: y.timeslots.map((z) => {
            return {
              startTime: z.startTime.toDate(),
              endTime: z.endTime.toDate(),
            };
          }),
          translatorEmails: y.translatorEmails.map((y) => y),
        };
      });
    },
    FLUSH_DRAFT_DATA(state) {
      state.draft = {
        description: "",
        eventTimes: [],
        isVideoEvent: true,
        langComb: [],
        maxParticipants: 2,
        name: "",
        organizer: "",
        paymentMethod: "",
        eventType: "video",
        livestreamType: "player",
        hybridType: "livestream",
        interpretingType: "device",
        interpretingParticipants: 2,
        externalEventLink: "",
        reqToSpeakMode: false,
        billNote: "",
        branding: {
          primaryColor: "#121C2E",
          secondaryColor: "#121C2E",
          tertiaryColor: "#121C2E",
          fourthColor: "#121C2E",
          brandLogo: "/assets/qondalogo.svg",
        },
        isSubscriptionEvent: false,
      };
      state.moderationPassword = "";
    },
    FLUSH_SURVEY_DATA(state) {
      state.survey = {
        questions: [],
        options: {
          timed: false,
          duration: 60,
          showResults: true,
          personalization: false,
        },
        persons: [],
        title: "",
        draft: true,
      };
    },
    SET_DRAFT_FIELD(state, arr) {
      let field = arr[0];
      let value = arr[1];
      state.draft[field] = value;
    },
    SET_MODERATION_PW(state, pw) {
      state.moderationPassword = pw;
    },
    SET_SURVEY_FIELD(state, arr) {
      let field = arr[0];
      let value = arr[1];
      state.survey[field] = value;
    },
  },
  actions: {
    async updateOrganizationId({ commit }, organizationId) {
      let doc = await db.collection("organizations").doc(organizationId).get();
      // console.log(doc.data());
      commit("SET_ORGANIZATION_ID", organizationId);
      commit("SET_ORGANIZATION_DATA", doc.data());
      commit("SET_ORGANIZATION_NAME", doc.data().legalName);
      commit("CHANGE_COLOR_SCHEME", doc.data().branding);
      commit("CHANGE_LOGO", doc.data().branding.logoUrl);
    },
    async flushOrganizationData({ commit }) {
      commit("SET_ORGANIZATION_ID", undefined);
      commit("SET_ORGANIZATION_DATA", {});
      commit("SET_ORGANIZATION_NAME", "");
      commit("CHANGE_COLOR_SCHEME", {
        primaryColor: "#121C2E",
        secondaryColor: "#121C2E",
        tertiaryColor: "#121C2E",
        fourthColor: "#121C2E",
      });
      commit("CHANGE_LOGO", "/assets/qondalogo.svg");
    },
    async logInUser({ commit }, { email, password }) {
      let { user } = await auth.signInWithEmailAndPassword(email, password);
      commit("SET_USER", user);
    },
    async logOutUser({ commit }) {
      await auth.signOut();
      commit("FLUSH_PERMISSIONS");
      commit("SET_USER", null);
    },
    async saveBrandingDraft(context, eventId) {
      try {
        let branding = cloneDeep(context.state.draft.branding);
        let docRef = db
          .collection("organizations")
          .doc(context.getters.getOrganizationId)
          .collection("events")
          .doc(eventId);
        await docRef.update({ branding: branding });
      } catch (err) {
        console.log();
        throw err;
      }
    },
    async saveAsDraft(context, eventId) {
      try {
        let data = cloneDeep(context.state.draft);
        data.draft = true;
        data.organizer = context.getters.getCurrentUser.uid;
        data.isSubscriptionEvent = context.getters.getOrganizationData.isSubscriber || false;
        let docRef = db
          .collection("organizations")
          .doc(context.getters.getOrganizationId)
          .collection("events")
          .doc(eventId);

        if (context.state.moderationPassword) {
          await docRef.collection("data").doc("moderationPw").set(
            {
              moderationPw: context.state.moderationPassword,
            },
            { merge: true },
          );
        }
        if (context.state.moderationPassword == null) {
          await docRef.collection("data").doc("moderationPw").delete();
        }

        await docRef.set(data, { merge: true });
        // workaround
        if (data.recordLangs) {
          await docRef.update({ recordLangs: data.recordLangs });
        }
      } catch (err) {
        console.log(err);
        throw err;
      }
    },
    async saveSurvey(context, surveyId) {
      try {
        let data = cloneDeep(context.state.survey);
        data.draft = true;
        data.uid = context.state.user.uid;
        let surveyRef;
        if (surveyId != null) {
          surveyRef = db.collection("surveys").doc(surveyId);
        } else {
          surveyRef = db.collection("surveys").doc();
        }
        await surveyRef.set(data);
        return surveyRef.id;
      } catch (error) {
        console.log(error);
        throw error;
      }
    },
  },
  getters: {
    getPrimaryColor(state) {
      return state.branding.primaryColor;
    },
    getSecondaryColor(state) {
      return state.branding.secondaryColor;
    },
    getTertiaryColor(state) {
      return state.branding.tertiaryColor;
    },
    getFourthColor(state) {
      return state.branding.fourthColor;
    },
    getBrandLogo(state) {
      return state.branding.brandLogo;
    },
    getOrganizationId(state) {
      return state.organizationId;
    },
    getOrganizationName(state) {
      return state.organizationName;
    },
    getOrganizationData(state) {
      return state.organizationData;
    },
    getCurrentUser(state) {
      // little workaround to trigger reactiveness
      return state.user ? auth.currentUser : auth.currentUser;
    },
    getPermissions(state) {
      return state.permissions;
    },
  },
  plugins: [firebaseAuthPlugin],

  modules: {
    mediasoup: {
      namespaced: true,
      state: () => ({
        mediasoupConfig: mediasoupConfig,
        testDataChannelOk: false,
        audioProducer: {
          id: null,
          paused: null,
          track: null,
          rtpParameters: null,
          codec: null,
          ready: false,
        },
        videoProducer: {
          id: null,
          deviceLabel: null,
          type: null,
          paused: null,
          track: null,
          rtpParameters: null,
          codec: null,
          ready: false,
        },
        screenProducer: {
          id: null,
          type: null,
          paused: null,
          track: null,
          rtpParameters: null,
          codec: null,
          ready: false,
        },
        testViewer: false,
        embed: false,
        externalVideoProducer: {},
        externalAudioProducer: {},
        peers: [],
        allPeers: [],
        activeSpeaker: { id: null, volume: null },
        microphones: [],
        cameras: [],
        currentCamId: null,
        currentMicId: null,
        micPaused: false,
        vidPaused: false,
        isConference: true,
        isPresentation: false,
        isSpotlight: false,
        isViewerLive: false,
        spotlightPeerId: null,
        listenFloor: true,
        consumeLang: "ENG",
        lastConsumeLang: null,
        hearOtherTranslator: false,
        currentPresentator: null,
        presenterId: null,
        floorVolume: 50,
        consumeVolume: 50,
        listenerMute: false,
        presentationVolume: 50,
        otherTranslatorVolume: 50,
        onboarding: true,
        displayName: "",
        clientTranslator: {
          lang1: null,
          lang2: null,
          currentOutputLang: null,
          bidirectional: false,
          translatorReady: false,
          requestHandover: false,
          willTake: false,
        },
        audioConnected: false,
        role: "host",
        produceLang: "ENG",
        onAir: false,
        mediasoupId: null,
        handRaised: false,
        videoReady: false,
        maxParticipantsLimit: 1,
        maxParticipantsNotification: false,
        openActiveHandoverNotification: false,
        activeRequestId: null,
        activeRequestAnswererId: null,
        activeRequestAnswererName: null,
        activeReqDisplayName: null,
        activeRequestAccepted: false,
        activeRequestRejected: false,
        openPassiveHandoverNotification: false,
        passiveRequestId: null,
        passiveRequestPending: false,
        passiveRequestAccepted: false,
        passiveRequestRejected: false,
        passiveReqDisplayName: null,
        websocketReady: false,
        reqToSpeakMode: true,
        reqToSpeakOngoing: false,
        sharing: false,
        changedWebcamTrack: false,
        forceTakeover: null,
        noTranslation: false,
        connectionType: "",
        browserName: null,
        browserDetails: {},
        allowedToSpeak: false,
        lastAlive: null,
        translatorsOnAir: false,
        transcriptionResult: `Waiting for subtitle ...`,
        audioContextAllowed: false,
        transcriptionStarted: false,
        transcriptionOnClicked: false,
        transcriptionOffClicked: false,
      }),
      mutations: {
        set_browserDetails(state, payload) {
          state.browserDetails = payload;
        },
        set_transcriptionOnClicked(state, payload) {
          state.transcriptionOnClicked = payload;
        },
        set_transcriptionOffClicked(state, payload) {
          state.transcriptionOffClicked = payload;
        },
        set_transcriptionStarted(state, payload) {
          state.transcriptionStarted = payload;
        },
        set_audioContextAllowed(state, payload) {
          state.audioContextAllowed = payload;
        },
        set_transcriptionResult(state, payload) {
          state.transcriptionResult = payload;
        },
        set_testDataChannelOk(state, payload) {
          state.testDataChannelOk = payload;
        },
        set_translatorsOnAir(state, payload) {
          state.translatorsOnAir = payload;
        },
        set_testViewer(state) {
          state.testViewer = true;
        },
        set_viewerLive(state, payload) {
          state.isViewerLive = payload;
        },
        set_embed(state, payload) {
          state.embed = payload;
        },
        set_lastAlive(state, payload) {
          // setting for myself
          if (state.mediasoupId === payload.id) {
            state.lastAlive = payload.lastAlive;
          }
          // setting for others
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.lastAlive = payload.lastAlive;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_noTranslation(state, payload) {
          state.noTranslation = payload;
        },
        set_forceTakeover(state, payload) {
          state.forceTakeover = payload;
        },
        set_maxParticipantsLimit(state, payload) {
          state.maxParticipantsLimit = payload;
        },
        set_maxParticipantsNotification(state, payload) {
          state.maxParticipantsNotification = payload;
        },
        set_audio_producer(state, payload) {
          state.audioProducer = { ...payload };
        },
        set_video_producer(state, payload) {
          state.videoProducer = { ...payload };
        },
        set_screen_producer(state, payload) {
          state.screenProducer = { ...payload };
        },
        set_video_producer_track(state, payload) {
          state.videoProducer.track = payload;
        },
        add_peer(state, peer) {
          state.peers.push({ ...peer });
        },
        add_to_all_peer(state, peer) {
          state.allPeers.push({ ...peer });
        },
        set_allPeers(state, payload) {
          state.allPeers = payload;
        },
        set_videoReady(state, payload) {
          state.videoReady = payload;
        },
        remove_peer(state, peerId) {
          let index = state.peers.findIndex((elem) => elem.id === peerId); //elem.id === peerId
          if (index >= 0) {
            state.peers.splice(index, 1);
            console.log("peer Removed");
          }
        },
        remove_from_allPeer(state, peerId) {
          let index = state.allPeers.findIndex((elem) => elem.id === peerId); //elem.id === peerId
          if (index >= 0) {
            state.allPeers.splice(index, 1);
            console.log("peer Removed");
          }
        },
        add_video_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.videoConsumer = { ...payload.consumer };
              return peer;
            } else {
              return peer;
            }
          });
        },
        add_screen_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.screenConsumer = { ...payload.consumer };
              return peer;
            } else {
              return peer;
            }
          });
        },
        add_screen_audio_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.screenAudioConsumer = { ...payload.consumer };
              return peer;
            } else {
              return peer;
            }
          });
        },
        add_audio_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.audioConsumer = {
                ...payload.consumer,
                ["ready"]: payload.ready,
              };
              //pause if not in consume lang
              if (peer.produceLang !== state.consumeLang) {
                (peer.audioConsumer.paused = true), console.warn("Consumer only paused in vuex");
              }
              return peer;
            } else {
              return peer;
            }
          });
        },
        remove_video_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.videoConsumer = {};
              return peer;
            } else {
              return peer;
            }
          });
        },
        remove_screen_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer.screenConsumer = {};
              return peer;
            } else {
              return peer;
            }
          });
        },
        remove_screen_audio_consumer(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.peerId) {
              peer?.screenAudioConsumer?._track.stop();
              peer.screenAudioConsumer = {};
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_spotlight_peerId(state, payload) {
          state.spotlightPeerId = payload;
        },
        set_spotlight(state) {
          state.isSpotlight = true;
          state.isConference = false;
          state.isPresentation = false;
        },
        remove_spotlight(state) {
          state.isSpotlight = false;
          state.isConference = true;
          state.isPresentation = false;
        },
        start_presentation(state, presenter) {
          state.currentPresentator = presenter;
          state.isConference = false;
          state.isPresentation = true;
          state.isSpotlight = false;
          state.spotlightPeerId = null;
        },
        stop_presentation(state) {
          state.isConference = true;
          state.isPresentation = false;
          state.currentPresentator = null;
          state.presenterId = null;
          state.isSpotlight = false;
          state.spotlightPeerId = null;
        },
        set_presentation_status(state, status) {
          state.isConference = !status;
          state.isPresentation = status;
        },
        set_presentator(state, payload) {
          state.currentPresentator = payload;
        },
        onboardingComplete(state) {
          state.onboarding = false;
        },
        set_microphones(state, payload) {
          state.microphones = payload;
        },
        set_cameras(state, payload) {
          state.cameras = payload;
        },
        toggle_mute(state) {
          state.micPaused = !state.micPaused;
        },
        set_mute(state, payload) {
          state.micPaused = payload;
        },
        set_video_mute(state, payload) {
          state.vidPaused = payload;
        },
        set_sharing(state, payload) {
          state.sharing = payload;
        },
        set_handRaised(state, payload) {
          if (state.mediasoupId === payload.id) {
            state.handRaised = payload.handRaised;
            console.log("status changed", state.handRaised);
          }
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.handRaised = payload.handRaised;
              return peer;
            } else {
              return peer;
            }
          });
        },
        toggle_video_mute(state) {
          state.vidPaused = !state.vidPaused;
        },
        set_displayName(state, payload) {
          state.displayName = payload;
        },
        set_clientTranslator(state, payload) {
          state.clientTranslator = payload;
        },
        set_clientTranslatorOut(state, payload) {
          state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.clientTranslator.currentOutputLang = payload.currentOutputLang;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_translatorReady(state, readyState) {
          state.clientTranslator.translatorReady = readyState;
        },
        set_requestHandover(state, payload) {
          state.clientTranslator.requestHandover = payload;
        },
        set_willTake(state, payload) {
          state.clientTranslator.willTake = payload;
        },
        set_cam_id(state, payload) {
          state.currentCamId = payload;
        },
        set_mic_id(state, payload) {
          state.currentMicId = payload;
        },
        set_consumeLang(state, payload) {
          state.consumeLang = payload;
        },
        set_relayLang(state, payload) {
          state.consumeLang = payload;
        },
        set_activeSpeaker(state, payload) {
          state.activeSpeaker = payload;
        },
        toggle_floor(state) {
          state.listenFloor = !state.listenFloor;
          if (state.listenFloor === true) {
            state.lastConsumeLang = state.consumeLang;
            state.consumeLang = null;
          } else {
            state.consumeLang = state.lastConsumeLang;
          }
        },
        set_listenFloor(state, payload) {
          state.listenFloor = payload;
        },
        set_floor(state, payload) {
          state.listenFloor = payload;
        },
        toggle_other_translator(state) {
          state.hearOtherTranslator = !state.hearOtherTranslator;
        },
        set_hearOtherTranslator(state, payload) {
          state.hearOtherTranslator = payload;
        },
        set_peer_produce_lang(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.produceLang = payload.produceLang.toUpperCase();
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_peer_consume_lang(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              // NUll CHECK
              if (payload.consumeLang) {
                peer.consumeLang = payload.consumeLang.toUpperCase();
              } else {
                peer.consumeLang = payload.consumeLang;
              }
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_peer_role(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.role = payload.role;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_passiveRequestPending(state, payload) {
          state.passiveRequestPending = payload;
        },
        set_passiveRequestAccepted(state, payload) {
          state.passiveRequestAccepted = payload;
        },
        set_passiveRequestRejected(state, payload) {
          state.passiveRequestRejected = payload;
        },
        set_openPassiveHandoverNotification(state, payload) {
          state.openPassiveHandoverNotification = payload;
        },
        set_activeRequestId(state, payload) {
          state.activeRequestId = payload;
        },
        set_activeRequestAnswererId(state, payload) {
          state.activeRequestAnswererId = payload;
        },
        set_activeRequestAnswererName(state, payload) {
          state.activeRequestAnswererName = payload;
        },
        set_activeReqDisplayName(state, payload) {
          state.activeReqDisplayName = payload;
        },
        set_openActiveHandoverNotification(state, payload) {
          state.openActiveHandoverNotification = payload;
        },
        set_activeRequestAccepted(state, payload) {
          state.activeRequestAccepted = payload;
        },
        set_activeRequestRejected(state, payload) {
          state.activeRequestRejected = payload;
        },
        set_passiveRequestId(state, payload) {
          state.passiveRequestId = payload;
        },
        set_passiveReqDisplayName(state, payload) {
          state.passiveReqDisplayName = payload;
        },
        set_peer_onAir(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.onAir = payload.onAir;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_peer_micPaused(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.micPaused = payload.micPaused;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_peer_vidPaused(state, payload) {
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.id) {
              peer.vidPaused = payload.vidPaused;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_peer_connectionType(state, payload) {
          // console.log("setconnectiontypepeers", state.peers);
          // console.log("payload", payload);
          if (state.mediasoupId !== payload.senderId) {
            state.peers = state.peers.map((peer) => {
              if (peer.id === payload.senderId) {
                peer.connectionType = payload.connectionType;
                // console.log("returning peer", peer);
                return peer;
              } else {
                // console.log("returning peer not midified", peer);

                return peer;
              }
            });
          }
          console.log("peeersagaing", state.peers);
        },
        set_connectionType(state, payload) {
          state.connectionType = payload;
        },
        // delete later
        // peer_switched_toHost(state, payload) {
        //   state.peers = state.peers.map(peer => {
        //     if (peer.id === payload) {
        //       peer.role = "host";
        //       return peer;
        //     } else {
        //       return peer;
        //     }
        //   });
        // },
        // peer_switched_toViewer(state, payload) {
        //   state.peers = state.peers.map(peer => {
        //     if (peer.id === payload) {
        //       peer.role = "viewer";
        //       return peer;
        //     } else {
        //       return peer;
        //     }
        //   });
        // },
        set_presentation_volume(state, payload) {
          state.presentationVolume = payload;
        },
        set_consume_volume(state, payload) {
          state.consumeVolume = payload;
        },
        set_floor_volume(state, payload) {
          state.floorVolume = payload;
        },
        set_listener_mute(state, payload) {
          state.listenerMute = payload;
        },
        set_otherTranslator_volume(state, payload) {
          state.otherTranslatorVolume = payload;
        },
        audio_connected(state) {
          state.audioConnected = true;
        },
        set_role(state, payload) {
          state.role = payload;
        },
        set_produce_lang(state, payload) {
          state.produceLang = payload;
        },
        set_onAir(state, payload) {
          state.onAir = payload;
        },
        set_mediasoupId(state, payload) {
          state.mediasoupId = payload;
        },
        changedWebcamTrack(state, payload) {
          state.changedWebcamTrack = payload;
        },
        set_websocketReady(state, payload) {
          state.websocketReady = payload;
        },
        set_browserName(state, payload) {
          state.browserName = payload;
        },
        reset(state) {
          state.peers = [];
          state.audioProducer = {
            id: null,
            paused: null,
            track: null,
            rtpParameters: null,
            codec: null,
            ready: false,
          };
          (state.videoProducer = {
            id: null,
            deviceLabel: null,
            type: null,
            paused: null,
            track: null,
            rtpParameters: null,
            codec: null,
            ready: false,
          }),
            (state.screenProducer = {
              id: null,
              type: null,
              paused: null,
              track: null,
              rtpParameters: null,
              codec: null,
              ready: false,
            });
        },
        set_reqToSpeakMode(state, payload) {
          state.reqToSpeakMode = payload;
        },
        set_reqToSpeakOngoing(state, payload) {
          // console.log("payload in store", payload);
          if (state.mediasoupId === payload.requesterId) {
            state.reqToSpeakOngoing = payload.reqToSpeakOngoing;
          }
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.requesterId) {
              peer.reqToSpeakOngoing = payload.reqToSpeakOngoing;
              return peer;
            } else {
              return peer;
            }
          });
        },
        set_allowedToSpeak(state, payload) {
          // setting it for myself
          // console.log("payload in store", payload);
          if (state.mediasoupId === payload.viewerId) {
            state.allowedToSpeak = payload.allowedToSpeak;
          }
          // setting it for others
          state.peers = state.peers.map((peer) => {
            if (peer.id === payload.viewerId) {
              peer.allowedToSpeak = payload.allowedToSpeak;
              return peer;
            } else {
              return peer;
            }
          });
        },
      },
      getters: {
        currentPresenter: (state) => {
          return state.currentPresentator;
        },
        presenterConsumerReady: (state) => {
          if (state.currentPresentator) {
            const presenter = state.peers.find((peer) => peer.id === state.currentPresentator);
            console.log("presenter ready", presenter?.screenConsumer?._paused);
            if (presenter?.screenConsumer?._paused === false && presenter?.screenConsumer?._track?.muted === false) {
              return presenter.screenConsumer._track;
            }
          }
        },
        getPresenterAudioConsumer: (state) => {
          if (state.currentPresentator) {
            const presenter = state.peers.find((peer) => peer.id === state.currentPresentator);
            if (
              presenter?.screenAudioConsumer?._paused === false &&
              presenter?.screenAudioConsumer?._track?.muted === false
            ) {
              return presenter.screenAudioConsumer._track;
            }
          }
        },
        getDisplayName: (state) => {
          return state.displayName;
        },
        getEmbed: (state) => {
          return state.embed;
        },
        noTranslation: (state) => {
          return state.noTranslation;
        },
        browserName: (state) => {
          return state.browserName;
        },
        forceTakeover: (state) => {
          return state.forceTakeover;
        },
        audioProducer: (state) => {
          return state.audioProducer;
        },
        videoProducer: (state) => {
          return state.videoProducer;
        },
        screenProducer: (state) => {
          return state.screenProducer;
        },
        videoReady: (state) => {
          return state.videoReady;
        },
        getPeers: (state) => {
          return state.peers;
        },
        getAllPeers: (state) => {
          let allPeers = [...state.peers];
          allPeers.push({
            id: state.mediasoupId,
            role: state.role,
            displayName: state.displayName,
            device: state.device,
            screenShare: state.screenShare,
            audio: state.audio,
            produceLang: state.produceLang,
            consumeLang: state.consumeLang,
            producers: [],
            consumers: [],
            audioConsumer: {},
            videoConsumer: {},
            onAir: state.onAir,
            micPaused: state.micPaused,
            vidPaused: state.vidPaused,
            isModerator: state.isModerator,
            lastAlive: state.lastAlive,
            connectionType: state.connectionType,
            handRaised: state.handRaised,
            reqToSpeakOngoing: state.reqToSpeakOngoing,
            allowedToSpeak: state.allowedToSpeak,
            clientTranslator: state.clientTranslator,
          });
          return allPeers;
        },
        getOnAirLangs: (state, getters) => {
          const onAirInterpreters = getters.getAllPeers.filter((peer) => {
            if (peer.role === "translator" && peer.onAir === true) {
              return true;
            }
          });
          let onAirLangs = [];
          for (const interpreter of onAirInterpreters) {
            onAirLangs.push(interpreter.clientTranslator.currentOutputLang);
          }
          // console.log("onairlangs", onAirLangs);
          return onAirLangs;
        },
        getOnAirLangsInPeers: (state, getters) => {
          const onAirInterpreters = getters.getPeers.filter((peer) => {
            if (peer.role === "translator" && peer.onAir === true) {
              return true;
            }
          });
          let onAirLangs = [];
          for (const interpreter of onAirInterpreters) {
            onAirLangs.push(interpreter.clientTranslator.currentOutputLang);
          }
          // console.log("onairlangs", onAirLangs);
          return onAirLangs;
        },
        getPeersCount: (state) => {
          return state.peers.length;
        },
        getHostsCount: (state) => {
          let hosts = state.peers.filter((peer) => peer.role === "host" || peer.role === "hybrid");
          return hosts.length;
        },
        getHosts: (state) => {
          return state.peers.filter((peer) => peer.role === "host" || peer.role === "hybrid");
        },
        getSpotlightPeerId: (state) => {
          return state.spotlightPeerId;
        },
        getMaxParticipantsLimit: (state) => state.maxParticipantsLimit,
        maxParticipantsNotification: (state) => {
          return state.maxParticipantsNotification;
        },
        getConnectionType: (state) => state.connectionType,
        isConference: (state) => {
          return state.isConference;
        },
        isPresentation: (state) => {
          return state.isPresentation;
        },
        isSpotlight: (state) => {
          return state.isSpotlight;
        },
        currentCamId: (state) => {
          return state.currentCamId;
        },
        currentMicId: (state) => {
          return state.currentMicId;
        },
        mediasoupId: (state) => {
          return state.mediasoupId;
        },
        floorVolume: (state) => {
          return state.floorVolume;
        },
        consumeVolume: (state) => {
          return state.consumeVolume;
        },
        presentationVolume: (state) => {
          return state.presentationVolume;
        },
        otherTranslatorVolume: (state) => {
          return state.otherTranslatorVolume;
        },
        micPaused: (state) => {
          return state.micPaused;
        },
        vidPaused: (state) => {
          return state.vidPaused;
        },
      },
    },
  },
});
